/**
 * AI CONTEXT
 * Purpose: inline "task line" under a roster row's student name — the student's
 *   open tasks, checkable at check-in without leaving the roster.
 * Visibility: open tasks AND (manual flag toggle ?? present-status)
 *   (getIsTaskLineOpen). The automatic state derives from synced data, so
 *   workstations agree and the line survives refresh; the flag pins it open
 *   (peek before arrival) or closed until the student's status changes.
 * Writes: toggles student.tasks via useToggleStudentTask.
 * Used by: AttendanceRow (admin front desk; teacher rows have no open tasks so
 *   this renders null there).
 * Refactor: none yet.
 */
import { Checkbox, Popover } from "antd"
import { motion, type TargetAndTransition } from "motion/react"
import { useEffect, useState } from "react"
import styled from "styled-components"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { getStudentTaskTypeLabel } from "#customTypes/composedTypes/studentTaskTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { AttendanceStatus } from "#customTypes/types"
import { useToggleStudentTask } from "#hooks/newHooks/useToggleStudentTask"
import { COLORS } from "#styles/colors"
import {
  getIsTaskLineOpen,
  getOpenTasks,
  getPresentHighlightPhase,
  PRESENT_FLASH_DURATION_MS,
  selectPrimaryOpenTask,
} from "#utils/taskHelpers"

const TASK_LINE_HEIGHT = 22

/** Row tint used by AttendanceRow for open-task rows; the line settles deeper. */
const OPEN_TASK_ROW_BACKGROUND = "#FFF7E6"
/** Static amber tint the line settles into (deeper than the row tint so it reads). */
const SETTLED_AMBER = `${COLORS.priority.SOON}2E`
/** Gentle red pulse peak — softer than StudentTaskCard's HIGHLIGHT_ANIMATION.flash. */
const PULSE_RED = "#FFCDD2"
const NO_RING = "0 0 0 0 rgba(244, 67, 54, 0)"

/**
 * Locally-observed arrival cue (cousin of StudentTaskCard's HIGHLIGHT_ANIMATION):
 * a brief FINITE pulse (3 total), then the settled amber tint. Remote machines
 * and page loads never see the pulse — they render `settled` directly.
 */
const TASK_LINE_ANIMATION: Record<"flash" | "settled", TargetAndTransition> = {
  flash: {
    backgroundColor: [SETTLED_AMBER, PULSE_RED, SETTLED_AMBER],
    boxShadow: [NO_RING, "0 0 0 3px rgba(244, 67, 54, 0.45)", NO_RING],
    transition: { duration: 1, ease: "easeInOut", repeat: 2 },
  },
  settled: {
    backgroundColor: SETTLED_AMBER,
    boxShadow: NO_RING,
    transition: { duration: 0.3 },
  },
}

/**
 * True while a locally-observed arrival should pulse the line. Mirrors
 * usePresentHighlightPhase in StudentTaskCard: a timeout flips the state once
 * the flash window elapses; a new timestamp (re-marked present) restarts it.
 * No highlight entry, or an entry already in its "glow" phase (page load,
 * remote machine), never flashes.
 */
const useTaskLineFlash = (markedPresentAt: number | undefined): boolean => {
  const [isFlashing, setIsFlashing] = useState(
    () =>
      markedPresentAt !== undefined &&
      getPresentHighlightPhase(markedPresentAt, Date.now()) === "flash"
  )

  useEffect(() => {
    if (markedPresentAt === undefined) {
      setIsFlashing(false)
      return
    }

    const initialPhase = getPresentHighlightPhase(markedPresentAt, Date.now())
    if (initialPhase === "glow") {
      setIsFlashing(false)
      return
    }

    setIsFlashing(true)
    const remainingMs = markedPresentAt + PRESENT_FLASH_DURATION_MS - Date.now()
    const timeout = setTimeout(() => setIsFlashing(false), remainingMs)
    return () => clearTimeout(timeout)
  }, [markedPresentAt])

  return isFlashing
}

const SLine = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  height: ${TASK_LINE_HEIGHT}px;
  padding: 0 8px;
  font-size: 11px;
  border-radius: 4px;
  border-left: 3px solid ${COLORS.priority.SOON};
  background: ${OPEN_TASK_ROW_BACKGROUND};
`

const SLineText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-grow: 1;
  min-width: 0;
  cursor: pointer;
`

const STypeLabel = styled.strong`
  flex-shrink: 0;
  white-space: nowrap;
`

const SCountChip = styled.span`
  flex-shrink: 0;
  background: ${COLORS.priority.SOON};
  color: white;
  border-radius: 8px;
  padding: 0 6px;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
`

const SCheckboxWrapper = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

/**
 * One fixed-height line under the student name. Exactly one open task: inline
 * checkbox + type label + truncated label; text click pops the full text.
 * Two or more: primary (oldest) task's labels + a "+N" chip; clicking the line
 * pops ALL open tasks as checkable rows. Checking down to one remaining
 * naturally re-renders to the single-task form.
 */
const AttendanceRowTaskLine = ({
  student,
  status,
}: {
  student: StudentConstructor
  status: AttendanceStatus
}): JSX.Element | null => {
  const { taskLineToggles, presentHighlights } = useAdminViewContext()
  const { toggleTask } = useToggleStudentTask(student.id)
  const isFlashing = useTaskLineFlash(presentHighlights[student.id])

  const openTasks = getOpenTasks(student.tasks)
  const isOpen = getIsTaskLineOpen({
    status,
    tasks: student.tasks,
    manualOpen: taskLineToggles[student.id],
  })
  const primaryTask = selectPrimaryOpenTask(openTasks)

  if (!isOpen || !primaryTask) return null

  const primaryTypeLabel = getStudentTaskTypeLabel(primaryTask.type)
  const extraCount = openTasks.length - 1

  const singleTaskPopoverContent = (
    <div
      data-testid="attendance-task-line-popover"
      style={{ maxWidth: 300, fontSize: 12 }}
    >
      <strong>{primaryTypeLabel}</strong>: {primaryTask.label}
    </div>
  )

  const allTasksPopoverContent = (
    <div data-testid="attendance-task-line-popover" style={{ maxWidth: 320 }}>
      <Flex col gap={6}>
        {openTasks.map((task) => (
          <div
            key={task.id}
            data-testid={`attendance-task-popover-task-${task.id}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
            }}
          >
            <Checkbox
              checked={task.isCompleted}
              onChange={() => toggleTask(task.id)}
            />
            <span>
              <strong>{getStudentTaskTypeLabel(task.type)}</strong>:{" "}
              {task.label}
            </span>
          </div>
        ))}
      </Flex>
    </div>
  )

  return (
    <AnimateInFlex animation={ANIMATION_BY_KEY.down} style={{ width: "100%" }}>
      <SLine
        data-testid={`attendance-task-line-${student.id}`}
        animate={
          isFlashing ? TASK_LINE_ANIMATION.flash : TASK_LINE_ANIMATION.settled
        }
      >
        {extraCount === 0 ? (
          <>
            <SCheckboxWrapper
              data-testid="attendance-task-line-checkbox"
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={primaryTask.isCompleted}
                onChange={() => toggleTask(primaryTask.id)}
              />
            </SCheckboxWrapper>
            <Popover trigger="click" content={singleTaskPopoverContent}>
              <SLineText>
                <STypeLabel>{primaryTypeLabel}</STypeLabel>
                <TextOverrun text={primaryTask.label} />
              </SLineText>
            </Popover>
          </>
        ) : (
          <Popover trigger="click" content={allTasksPopoverContent}>
            <SLineText>
              <STypeLabel>{primaryTypeLabel}</STypeLabel>
              <TextOverrun text={primaryTask.label} />
              <SCountChip data-testid="attendance-task-line-count">
                +{extraCount}
              </SCountChip>
            </SLineText>
          </Popover>
        )}
      </SLine>
    </AnimateInFlex>
  )
}

export default AttendanceRowTaskLine
